/**
 * `<RulingPalette>` — the GM's ad-hoc ruling band in the **PLAY** inspector: the unit sheet's RULE
 * tray (`unit-sheet-redesign` 6.2). The four accuracy/difficulty presets sit in a 2×2 matrix (+1
 * ACCURACY / +1 DIFFICULTY by NEXT ATTACK / UNTIL END OF TURN), the rest are chips under it, and
 * the armed row opens under those. `<GmDamageRow>` below is the DAMAGE tray.
 *
 * **The five-second attention budget is the design constraint, and it is testable, not
 * aspirational: from the GM's PLAY view with a unit selected, a preset must fire in TWO
 * clicks.** More than that and the GM goes back to saying it out loud, and the feature — however
 * correct — goes unused. That budget is why this lives on the PLAY inspector rather than the EDIT
 * one: `EditInspectorBody` is the authoring desktop, not where a GM sits mid-encounter, and
 * reaching it is already more than two clicks. (The EDIT inspector is the right home for a
 * parameterized custom-ruling builder, which is not on the five-second clock.)
 *
 * **Two clicks, counted honestly** (with the RULE tray open, which the sheet remembers per window):
 * click the preset → the reason field focuses with the preset's
 * default reason pre-filled → click GRANT. A GM who wants to type their own reason types it
 * between those two clicks; a GM in a hurry just clicks twice. The reason is never optional,
 * because a ruling with no stated reason is the verbal ruling everyone forgets by round 3 — but
 * "required" must not mean "blocks the fast path", hence the pre-fill.
 *
 * **GM-only for v1.** A player-side "I want to try something" request channel is a deliberate
 * follow-on, and the command shape does not preclude it (`grant_ruling` is GM-tier by permission,
 * not by payload).
 *
 * **The GM's own saved rulings render here beside the presets.** One list,
 * one row component, one `grant_ruling` dispatch — the two kinds are indistinguishable at fire
 * time on purpose, so the two-click budget above is proven once for both rather than twice or
 * only for one. Authoring is the `KEEP` affordance on this arm: a GM composes a ruling by firing
 * one and keeping it. **Management (rename/delete) lives on the EDIT surface**
 * (`SavedRulingLibrary`), where authoring belongs; it is not on this five-second clock.
 *
 * The palette is a **form over the existing atom vocabulary, never an expression field**
 * — every button below is a fixed `RulingSpec`, so a GM structurally cannot express anything
 * `compileRuling` does not emit. Adding a free-text expression or condition input here reopens a
 * settled atoms-vs-scripting question; record it as a finding rather than bolting on a hatch.
 */
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../Icon";
import { tipAttrs } from "../tooltip";
import { Fragment, useState } from "react";
import { activeActor } from "@lancer-vtt/domain";
import type { Combatant, EncounterState, RulingSpec, SavedRuling, StatusExpiry } from "@lancer-vtt/domain";
import type { EncounterIntent, GmDamageType } from "@lancer-vtt/shared";
import { GM_DAMAGE_TYPES, apMeaningful, damageDestination, gmDamageIntent, parseAmount } from "./gmDamage";
import { useGameSessionContext } from "../game/GameSessionProvider";
import { LINK_DOWN_NOTE } from "../roomWs";
import { mintSavedRulingId, savedRulingFrom } from "./savedRulings";
import type { EncounterDispatch } from "./StatusEditor";

/**
 * One palette button: a fixed spec, the status name it rides, and a default reason.
 *
 * **Structurally identical to a {@link SavedRuling} minus its `id`** — and that is load-bearing
 * rather than coincidental. Because the shapes match, the palette renders
 * `[...PRESETS, ...savedRulings]` through ONE row component into ONE `grant_ruling` dispatch;
 * two parallel paths would be the duplicated wiring that diverges silently, and would force the
 * two-click acceptance criterion to be proven twice or cover only one kind.
 */
type Preset = {
  /** The button's own stable id (also its React key). */
  id: string;
  /** The button face — short enough to scan mid-encounter. */
  label: string;
  /** The status name the granted instance takes. Must not collide with a reserved name. */
  status: string;
  /** A default reason, pre-filled so the fast path is genuinely two clicks. */
  defaultReason: string;
  ruling: Omit<RulingSpec, "reason">;
  expiry?: StatusExpiry;
  /**
   * Grant with `stack`, so firing it on a unit that already carries it raises the count
   * ("Inspired 2", +2 accuracy) instead of replacing it. Set on the four accuracy/difficulty
   * matrix presets only; the damage and advisory presets and saved rulings still replace.
   */
  stack?: boolean;
};

/**
 * A palette row, whichever kind it is — the unified type the render/fire path operates on.
 * `saved` is the ONLY thing that distinguishes them, and it is read in exactly one place: the
 * EDIT-surface manager decides what it may manage. **At fire time the two kinds are
 * indistinguishable, deliberately** — a GM mid-encounter should not have to care which sort of
 * row they are clicking, and making them look different would tax the five-second budget for
 * nothing.
 */
type PaletteRow = Preset & { saved: boolean };

/** The matrix's rows (+1 ACCURACY, +1 DIFFICULTY) by its columns (NEXT ATTACK, UNTIL END OF TURN), as preset ids. */
const MATRIX: readonly { label: string; cells: readonly [string, string] }[] = [
  { label: "+1 ACCURACY", cells: ["acc-next", "acc-turn"] },
  { label: "+1 DIFFICULTY", cells: ["diff-next", "diff-turn"] },
];
const MATRIX_IDS: ReadonlySet<string> = new Set(MATRIX.flatMap((r) => r.cells));

/**
 * **The seven presets stay CODE-SHIPPED, versioned with the app — they are never rows in
 * `GameState`**. Not for symmetry with saved rulings, and not to avoid
 * drift: because of **deletability**. If presets were library rows, a GM could delete their own
 * floor with a misclick and there would be no restore path short of hand-editing a world file.
 * The presets are what make this palette work on a fresh world with zero setup, so they must be
 * un-deletable **by construction, not by discipline** — which is why the EDIT-surface manager
 * can only reach rows with `saved: true`.
 *
 * The ruling presets. **Every one of these must compile to mechanics that genuinely fire** — a
 * button whose grant silently does nothing is a UI lying to the GM about the rules, which is worse
 * than a missing button.
 *
 * Note an asymmetry no new preset can fix: `extra_damage` has **no negative form**, and the only
 * damage-reduction
 * primitive in the engine (`reduce_pending_damage`) is reaction-window only — so "−1d6 damage"
 * has no primitive and lands as an advisory chip carrying the GM's reason, not as engine math.
 */
const PRESETS: readonly Preset[] = [
  {
    id: "acc-next",
    stack: true,
    label: "+1 Accuracy · next attack",
    status: "Inspired",
    defaultReason: "good idea — take the bonus",
    ruling: { accuracy: 1, consumeOnAttack: true },
  },
  {
    id: "acc-turn",
    stack: true,
    label: "+1 Accuracy · until end of turn",
    status: "Advantage",
    defaultReason: "the position earns you this",
    ruling: { accuracy: 1 },
    expiry: { kind: "turn", edge: "end", who: "target" },
  },
  {
    id: "dmg-next",
    label: "+1d6 damage · next attack",
    status: "Solid Hit",
    defaultReason: "that shot finds the weak point",
    ruling: { extraDamage: { dice: { count: 1, sides: 6 } }, consumeOnAttack: true },
  },
  {
    id: "diff-next",
    stack: true,
    label: "+1 Difficulty · next attack",
    status: "Rattled",
    defaultReason: "that hit shook you",
    ruling: { accuracy: -1, consumeOnAttack: true },
  },
  {
    id: "diff-turn",
    stack: true,
    label: "+1 Difficulty · until end of turn",
    status: "Off Balance",
    defaultReason: "you are still recovering from that",
    ruling: { accuracy: -1 },
    expiry: { kind: "turn", edge: "end", who: "target" },
  },
  // The two advisory presets. Their labels say "advisory" out loud, because the one thing this
  // palette must never do is imply engine math it is not applying — a GM who thinks a button
  // took a die off the damage will build a turn around it.
  {
    id: "dmg-penalty",
    label: "Damage penalty · advisory",
    status: "Glancing Blow",
    defaultReason: "call it a d6 less — it barely connected",
    // A damage penalty has NO primitive: `extra_damage` has no negative form, and the only
    // damage-reduction atom (`reduce_pending_damage`) is reaction-window only. So this is
    // honestly an advisory, and the label says so rather than promising math.
    ruling: {},
  },
  {
    id: "advisory",
    label: "Advisory ruling",
    status: "Table Call",
    defaultReason: "",
    // Deliberately no default reason: an advisory ruling IS its reason, so pre-filling one
    // would put words in the GM's mouth. This is the one preset that costs a typed sentence,
    // and it should — there is nothing else to it.
    ruling: {},
  },
];


/**
 * The **budget grants** — a separate row from the presets
 * above, because they are a different kind of thing and pretending otherwise would mislead the GM.
 * A ruling rides a status: it has a duration, a reason the player can read, and it undoes as a
 * status. A budget grant is an imperative one-shot against the **current activation** — no
 * duration to give it, nothing to hang a reason on, and its inverse is `restore_budget`.
 *
 * They are therefore ONE click, not two: there is no reason field to fill in. That is not a
 * shortcut around the "always state a reason" rule — it is that these grant nothing a player
 * needs explained after the fact. The GM says "take another quick action" out loud; the button
 * just makes it real in the budget.
 *
 * **Only enabled while the unit is the acting one**, mirroring the server's own gate rather than
 * re-enforcing it (the house rule: rules live once in the reducer/referee, surfaced twice). The
 * referee refuses a grant aimed at a non-acting unit because the reducer writes the ACTING
 * unit's budget and ignores the named combatant entirely — so an ungated button would silently
 * help the wrong unit.
 */
type BudgetGrant = {
  id: string;
  label: string;
  title: string;
  command: (actorId: string, combatantId: string, speed: number) => Parameters<EncounterDispatch>[0];
};

const BUDGET_GRANTS: readonly BudgetGrant[] = [
  {
    id: "quick",
    label: "+1 Quick action",
    title: "Grant one extra Quick action for this activation",
    command: (actorId, combatantId) => ({ type: "grant_action_allowance", actorId, combatantId }),
  },
  {
    id: "move",
    label: "+Speed movement",
    title: "Grant this unit its Speed again in movement (Boost's arithmetic, none of Boost's costs)",
    // `speed` is passed in rather than read here so the button grants THIS unit's Speed — the
    // command records the literal number, so an undo/replay reproduces the exact grant even if
    // the unit's base changes later (`edit_combatant_base` is a live GM gesture).
    command: (actorId, combatantId, speed) => ({ type: "grant_move", actorId, combatantId, cells: speed }),
  },
];

export function RulingPalette({
  combatant,
  state,
  actorId,
  dispatch,
}: {
  combatant: Combatant;
  /** The live encounter state — read ONLY to mirror the server's acting-unit gate as an affordance. */
  state: EncounterState;
  actorId: string;
  dispatch: EncounterDispatch;
}) {
  const isActing = activeActor(state.activation) === combatant.id;
  const session = useGameSessionContext();
  const [armed, setArmed] = useState<PaletteRow | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  /** The label the GM is typing into the save affordance, or null while it's closed. */
  const [savingLabel, setSavingLabel] = useState<string | null>(null);

  // THE one render/fire list. Presets first — they are the zero-setup floor and a GM's
  // muscle memory should find them in a stable place — then the GM's own rows. Both kinds flow
  // through the same row component below into the same `grant_ruling` dispatch.
  const rows: PaletteRow[] = [
    ...PRESETS.map((p) => ({ ...p, saved: false })),
    ...session.savedRulings.map((r) => ({ ...r, saved: true })),
  ];

  function arm(p: PaletteRow) {
    setArmed(p);
    setReason(p.defaultReason);
    setError(null);
    setSavingLabel(null);
  }

  /**
   * Keep the ruling the GM has armed — the creation gesture, and the ONLY way a saved ruling
   * is authored. There is deliberately no composer: a GM builds a ruling by firing one and
   * saving it, so this affordance is the whole authoring surface.
   *
   * Re-saving a row that is ALREADY saved keeps its id, so this doubles as "fix the one I
   * saved" — the same overwrite-is-edit rule the command has.
   */
  function keep() {
    if (armed === null || savingLabel === null) return;
    const label = savingLabel.trim();
    if (label === "") {
      setError("a saved ruling needs a name — it becomes the button face");
      return;
    }
    const id = armed.saved
      ? armed.id
      : mintSavedRulingId(label, session.savedRulings.map((r) => r.id));
    const sent = session.saveRuling(
      savedRulingFrom({
        id,
        label,
        status: armed.status,
        // The reason currently in the field, not the row's original default: the GM just typed
        // the sentence they want, and that is the better pre-fill for next time.
        reason: reason.trim(),
        ruling: armed.ruling,
        ...(armed.expiry !== undefined ? { expiry: armed.expiry } : {}),
      }),
    );
    // Link down: keep the name field open with the note, so the GM retries without retyping.
    if (!sent) {
      setError(LINK_DOWN_NOTE);
      return;
    }
    setSavingLabel(null);
  }

  function grant() {
    if (armed === null) return;
    const trimmed = reason.trim();
    // Mirror the server's own requirement as an affordance rather than re-enforcing it: the
    // referee gate is the wall, this just stops the GM firing a command that will bounce.
    if (trimmed === "") {
      setError("a ruling needs a stated reason — the player sees it");
      return;
    }
    const r = dispatch({
      type: "grant_ruling",
      actorId,
      combatantId: combatant.id,
      status: armed.status,
      ruling: { reason: trimmed, ...armed.ruling },
      ...(armed.expiry !== undefined ? { expiry: armed.expiry } : {}),
      ...(armed.stack === true ? { stack: true } : {}),
    });
    if (!r.ok) {
      setError(r.error);
      return;
    }
    // NOTE, so nobody reads the line above as complete coverage: `dispatch` returns the LOCAL
    // apply's verdict. The referee-tier gates (reserved name, unreachable facet) run on the
    // server, so their refusal arrives later on the rejection-feedback channel — the shipped
    // per-socket relay — not here. The palette's own presets can never trip either gate (their
    // names are not reserved and their specs are all reachable), so this is not a live gap today;
    // it becomes one the moment a GM can type a status name here. Wire any such band to the
    // rejection channel.
    setArmed(null);
    setReason("");
    setError(null);
    setSavingLabel(null);
  }

  const byId = new Map(rows.map((p) => [p.id, p]));
  const presetButton = (p: PaletteRow, face: string) => (
    <button
      type="button"
      className={`unit-sheet-ruling-preset${armed?.id === p.id ? " is-armed" : ""}${p.saved ? " is-saved" : ""}`}
      onClick={() => arm(p)}
      aria-label={p.label}
      title={`Grant "${p.status}" to ${combatant.name}`}
    >
      {face}
    </button>
  );

  return (
    <div className="unit-sheet-rulings">
      {/* ONE list, ONE row component, ONE dispatch — presets and the GM's own saved rows
          are rendered by the same code and are indistinguishable to click. The matrix holds the
          four accuracy/difficulty presets; every other row is a chip. The only visual difference
          between a preset and a saved row is a marker class, which exists for the EDIT-surface
          manager's benefit and carries no behaviour. */}
      <div className="unit-sheet-rule-mx" role="group" aria-label="accuracy and difficulty">
        <span />
        <span className="unit-sheet-rule-mx-h">NEXT ATTACK</span>
        <span className="unit-sheet-rule-mx-h">UNTIL END OF TURN</span>
        {MATRIX.map((r) => (
          <Fragment key={r.label}>
            <span className="unit-sheet-rule-mx-rh">{r.label}</span>
            {r.cells.map((id) => {
              const p = byId.get(id)!;
              return <Fragment key={id}>{presetButton(p, armed?.id === id ? "+ GRANT ✓" : "+ GRANT")}</Fragment>;
            })}
          </Fragment>
        ))}
      </div>
      <ul className="unit-sheet-rule-chips">
        {rows
          .filter((p) => !MATRIX_IDS.has(p.id) || p.saved)
          .map((p) => (
            <li key={`${p.saved ? "saved" : "preset"}:${p.id}`}>{presetButton(p, p.saved ? `★ ${p.label}` : p.label)}</li>
          ))}
        {/* The budget grants share the chip row but are one click: see {@link BUDGET_GRANTS}. */}
        {BUDGET_GRANTS.map((g) => (
          <li key={g.id}>
            <button
              type="button"
              className="unit-sheet-ruling-preset is-budget"
              disabled={!isActing}
              title={isActing ? g.title : `${combatant.name} is not the acting unit — an extra action would land on whoever is`}
              onClick={() => {
                const r = dispatch(g.command(actorId, combatant.id, combatant.base.speed));
                setError(r.ok ? null : r.error);
              }}
            >
              {g.label}
            </button>
          </li>
        ))}
      </ul>
      {armed !== null && (
        <div className="unit-sheet-ruling-arm">
          <span className="unit-sheet-rule-armed">{armed.label}</span>
          <input
            className="unit-sheet-ruling-reason"
            value={reason}
            autoFocus
            aria-label="ruling reason"
            placeholder="why?"
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") grant();
              if (e.key === "Escape") setArmed(null);
            }}
          />
          <button type="button" className="unit-sheet-ruling-grant" onClick={grant}>
            GRANT
          </button>
          {/* "Keep this one" — the creation gesture. Deliberately OUTSIDE the two-click fast
              path: it sits beside GRANT rather than in front of it, so a GM in a hurry never
              pays for it, and a GM who just invented something good can keep it in one more
              click. Opening it pre-fills the row's own label, which is right for both cases:
              re-saving an existing row keeps its name, and keeping a preset-derived ruling
              starts from the preset's wording rather than a blank field. */}
          <button
            type="button"
            className="unit-sheet-ruling-keep"
            onClick={() => setSavingLabel(savingLabel === null ? armed.label : null)}
            aria-label={armed.saved ? "update saved ruling" : "save this ruling"}
            title={armed.saved ? `Update the saved ruling "${armed.label}"` : "Keep this ruling in your library for next session"}
          >
            {armed.saved ? "UPDATE" : "KEEP"}
          </button>
          <button type="button" className="unit-sheet-ruling-cancel" onClick={() => setArmed(null)} aria-label="cancel ruling" {...tipAttrs({ label: "Cancel ruling" })}>
            <Icon icon={faXmark} />
          </button>
        </div>
      )}
      {armed !== null && savingLabel !== null && (
        <div className="unit-sheet-ruling-save">
          <input
            className="unit-sheet-ruling-savename"
            value={savingLabel}
            autoFocus
            aria-label="saved ruling name"
            placeholder="name this ruling"
            onChange={(e) => setSavingLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") keep();
              if (e.key === "Escape") setSavingLabel(null);
            }}
          />
          <button type="button" className="unit-sheet-ruling-saveok" onClick={keep}>
            SAVE
          </button>
        </div>
      )}
      {error !== null && <p className="unit-sheet-ruling-err">{error}</p>}
    </div>
  );
}

/**
 * `<GmDamageRow>` — the GM-adjudicated **DAMAGE** tray (`unit-sheet-redesign` 6.2): amount, type,
 * AP, APPLY, and a one-line hint of where that type lands.
 *
 * A tray of its own, deliberately not a preset in the RULE tray, and the distinction is the
 * five-second budget rather than taste. A preset fires in two clicks; damage is amount + type +
 * APPLY, which is three interactions and cannot honestly be squeezed into that budget. Making it
 * a preset would have meant either lying about the budget or dropping the type picker, and a
 * selected type is the whole feature.
 *
 * It is also the only GM tool that sends an INTENT rather than a command: this is a real
 * resolution that runs armor, the Overshield drain, the cascades and destruction, which is exactly
 * why a GM should reach for it instead of typing HP.
 */
export function GmDamageRow({
  combatant,
  sendIntent,
}: {
  combatant: Combatant;
  /**
   * The intent channel. GM damage is a **resolution** — it draws dice, cascades, and can halt on a
   * System Trauma prompt — so it rides `sendIntent` like an attack does, never `dispatch`.
   */
  sendIntent: (intent: EncounterIntent) => boolean;
}) {
  const [dmgAmount, setDmgAmount] = useState("");
  const [dmgType, setDmgType] = useState<GmDamageType>("kinetic");
  const [dmgAp, setDmgAp] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fire the GM's adjudicated damage. Unlike every other control here this sends an INTENT, so
   * there is no local verdict to read — `sendIntent` returns only whether the socket accepted the
   * send. A rules rejection (and a System Trauma prompt) arrive later on their own channels, which
   * is the same contract every attack on this surface already lives under.
   */
  function applyDamage() {
    const parsed = parseAmount(dmgAmount);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    const sent = sendIntent(gmDamageIntent({ target: combatant.id, amount: parsed.amount, damageType: dmgType, ap: dmgAp }));
    if (!sent) {
      setError("not connected — the adjudication was not sent");
      return;
    }
    // Clear the amount but KEEP the type and AP tick: a GM adjudicating a hazard usually deals the
    // same kind of damage to several units in a row, and re-picking the type each time is the
    // friction this band exists to remove. The amount clears because reusing it silently would be
    // the dangerous half of that convenience.
    setDmgAmount("");
    setError(null);
  }

  return (
    <div className="unit-sheet-damage">
      <div className="unit-sheet-damage-row">
        <input
          className="unit-sheet-damage-amount"
          value={dmgAmount}
          inputMode="numeric"
          aria-label="damage amount"
          placeholder="dmg"
          onChange={(e) => setDmgAmount(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") applyDamage();
          }}
        />
        <select className="unit-sheet-damage-type" value={dmgType} aria-label="damage type" onChange={(e) => setDmgType(e.target.value as GmDamageType)}>
          {GM_DAMAGE_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        {/* Disabled rather than hidden for burn/heat: a control that vanishes reads as a bug,
            and the title says WHY it is inert instead of leaving the GM to guess. */}
        <label
          className={`unit-sheet-damage-ap${apMeaningful(dmgType) ? "" : " is-inert"}`}
          title={apMeaningful(dmgType) ? "Armor-piercing — ignore the target's armor" : `${dmgType} ignores armor already, so AP changes nothing`}
        >
          <input type="checkbox" checked={dmgAp && apMeaningful(dmgType)} disabled={!apMeaningful(dmgType)} aria-label="armor piercing" onChange={(e) => setDmgAp(e.target.checked)} />
          AP
        </label>
        <button type="button" className="unit-sheet-damage-apply" onClick={applyDamage} title={`Apply ${dmgAmount || "N"} ${dmgType} to ${combatant.name} through the real damage path`}>
          APPLY
        </button>
      </div>
      {/* The three routing destinations genuinely differ, and a GM picking heat expecting HP
          loss has mis-adjudicated rather than mis-clicked. One line, read off the model. */}
      <span className="unit-sheet-damage-hint">{damageDestination(dmgType)}</span>
      {error !== null && <p className="unit-sheet-ruling-err">{error}</p>}
    </div>
  );
}
